import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { liquorService } from '../services/liquorService';
import { fetchSubcounties } from '../services/subcountyService';
import { 
    getFinancialYearData, formatToLocalTime2, formatToLocalTime,
    formatTodayTitle2, formatCurrentMonthTitle2, getFYEndYear, getFYStartYear, 
    formatToLocalTime3
} from '../utils/dateHelper';
import { Beer, RefreshCcw, TrendingUp, Calendar, Clock, LayoutDashboard, CheckCircle2, BarChart3 } from 'lucide-react';

const LiquorDashboard = () => {
    const { user } = useAuth();
    const fyOptions = getFinancialYearData();
    const fyStartYear = getFYStartYear(fyOptions.current);
    const fyEndYear = getFYEndYear(fyOptions.current);

    const [selectedFY, setSelectedFY] = useState(fyOptions.current);
    const [selectedSC, setSelectedSC] = useState(user?.sub_county_id || '');
    const [subcounties, setSubcounties] = useState([]);
    const [data, setData] = useState(null);
    const [categoryData, setCategoryData] = useState({});
    const [loading, setLoading] = useState(true);
    const [nextRefresh, setNextRefresh] = useState(10); // minutes remaining for next auto-refresh

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const scId = selectedSC === '' ? null : selectedSC;
            const [stats, items] = await Promise.all([
                liquorService.getDashboardStats(selectedFY, scId),
                liquorService.fetchLiquorItemReport({ financial_year: selectedFY, sub_county_id: scId })
            ]);
            setData(stats);
            console.log("last updated:", stats?.lastUpdated);
            setCategoryData(items);
        } catch (err) {
            console.error("Liquor Dashboard Error:", err);
        } finally {
            setLoading(false);
        }
    }, [selectedFY, selectedSC]);

    useEffect(() => {
        fetchSubcounties().then(setSubcounties);
    }, []);

    useEffect(() => { 
        loadData(); 
    }, [loadData]);
    
    useEffect(() => {
        // 1. Define the 10-minute interval (10 minutes * 60 seconds * 1000 milliseconds)
        const REFRESH_INTERVAL = 10 * 60 * 1000;

        const intervalId = setInterval(() => {
            console.log("Auto-refreshing Liquor Dashboard data...");
            //loadDashboardData();
            loadData();
        }, REFRESH_INTERVAL);

        // 2. Cleanup: Clear the interval if the component unmounts
        return () => {
            clearInterval(intervalId);
            console.log("Liquor Dashboard auto-refresh stopped.");
        };
        
    }, [loadData]);
    
    useEffect(() => {
        const timer = setInterval(() => {
            setNextRefresh((prev) => (prev <= 1 ? 10 : prev - 1));
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    if (loading && !data) return <div className="p-10 text-center animate-pulse">Synchronizing Liquor Data...</div>;

    return (
        <div className="space-y-4 animate-fadeIn">
            <DashboardHeader 
                selectedFY={selectedFY} setSelectedFY={setSelectedFY}
                selectedSC={selectedSC} setSelectedSC={setSelectedSC}
                subcounties={subcounties} loadData={loadData}
                lastUpdated={data?.lastUpdated} nextRefresh={nextRefresh}
                loading={loading} user={user} fyOptions={fyOptions}
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <StatCard 
                    title="Total Liquor Apps" 
                    value={data?.allBusinesses}
                    subValue={`New: ${data?.newBizH1 ? data?.newBizH1.toLocaleString() : 0} (H1) | ${data?.newBizH2 ? data?.newBizH2.toLocaleString() : 0} (H2)`}  
                    icon={<Beer size={18}/>} 
                    color="amber" 
                />
                <StatCard 
                    title={`Today's Revenue (${data?.lastUpdated ? formatTodayTitle2(data.lastUpdated) : '---'})`} 
                    value={data?.todayRev}
                    subValue={data?.todayRev === 0 ? `0 Liquor Invoices Paid` : `${data?.todayCount} Liquor Invoices Paid`} 
                    isMoney 
                    icon={<Clock size={16}/>} 
                    color="emerald" 
                />
                <StatCard 
                    title={`This Month (${data?.lastUpdated ? formatCurrentMonthTitle2(data.lastUpdated) : '---'})`} 
                    value={data?.monthRev}
                    subValue={data?.monthRev === 0 ? `0 Liquor Invoices Paid` : `${data?.monthCount} Liquor Invoices Paid`} 
                    isMoney 
                    icon={<Calendar size={16}/>} 
                    color="indigo" 
                />
                <StatCard 
                    title="FY Paid Total" 
                    value={data?.paidRevTotal} 
                    subValue={data?.paidRevTotal === 0 ? `0 Liquor Invoices Paid` : `${data?.paidCountTotal} Total Liquor Invoices Paid`}
                    isMoney 
                    icon={<TrendingUp size={16}/>} 
                    color="blue" 
                />
            </div>
            {/* Inside LiquorDashboard return statement */}
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-2 mt-2 px-1">
                
                {/* Pipeline Table: Occupies 2 columns on large screens */}
                <div className="lg:col-span-3">
                    <LiquorPerformanceTable 
                        title="Liquor License Approval Pipeline" 
                        data={data}
                        fyStartYear={fyStartYear}
                        fyEndYear={fyEndYear} 
                    />
                </div>

                {/* Revenue Table: Occupies 1 column on large screens */}
                <div className="lg:col-span-3">
                    <PerformanceTable 
                        title="Liqour License Revenue Collection Performance"
                        rows={[
                            { 
                                label: 'Paid (Revenue)', 
                                color: 'text-emerald-600',
                                isMoney: true, 
                                h1Value: data?.paidRevH1,
                                h2Value: data?.paidRevH2,
                                total: data?.paidRevTotal 
                            },
                            { 
                                label: 'Paid (Invoices)', 
                                color: 'text-emerald-600',
                                isMoney: false, 
                                h1Value: data?.paidCountH1,
                                h2Value: data?.paidCountH2,
                                total: data?.paidCountTotal 
                            },
                            { 
                                label: 'Pending (Revenue)', 
                                color: 'text-red-600', 
                                isMoney: true,
                                h1Value: data?.unpaidRevH1,
                                //h2Value: data?.unpaidRevH2,
                                h2Value: data?.unpaidCountH2 > 10 ? data?.unpaidRevH2 - (3000 * data?.unpaidCountH2) : data?.unpaidRevH2,
                                //total: data?.unpaidRevTotal
                                total: data?.unpaidCountH2 > 10 ? data?.unpaidRevTotal - (3000 * data?.unpaidCountH2) : data?.unpaidRevTotal
                            },
                            { 
                                label: 'Pending (Invoices)', 
                                color: 'text-red-600', 
                                isMoney: false,
                                h1Value: data?.unpaidCountH1,
                                h2Value: data?.unpaidCountH2,
                                total: data?.unpaidCountTotal 
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               <div className="lg:col-span-1">
                    <PerformanceTable 
                        title="Liquor Application Fee Pipeline Performance"
                        rows={[
                            { 
                                label: 'Paid Applications', 
                                color: 'text-emerald-600', 
                                isMoney: false,
                                h1Value: data?.depAppCountH1,
                                h2Value: data?.depAppCountH2,
                                total: data?.depAppCountTotal 
                            },
                            { 
                                label: 'Unpaid Applications', 
                                color: 'text-red-600', 
                                isMoney: false,
                                h1Value: data?.depAppUnpaidCountH1,
                                h2Value: data?.depAppUnpaidCountH2,
                                total: data?.depAppUnpaidCountTotal 
                            }, 
                        ]}
                    />
                </div> 
                <div className="lg:col-span-1">
                    <PerformanceTable 
                        title="Liquor Public Health Application Fee Pipeline Performance"
                        rows={[
                            { 
                                label: 'Paid Applications', 
                                color: 'text-emerald-600',
                                isMoney: false, 
                                h1Value: data?.depPHCountH1,
                                h2Value: data?.depPHCountH2,
                                total: data?.depPHCountTotal 
                            },
                            { 
                                label: 'Unpaid Applications', 
                                color: 'text-red-600',
                                isMoney: false, 
                                h1Value: data?.depPHUnpaidCountH1,
                                h2Value: data?.depPHUnpaidCountH2,
                                total: data?.depPHUnpaidCountTotal 
                            }, 
                        ]}
                    />
                </div> 
            </div>
            {/*<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Object.entries(categoryData).map(([category, items]) => (
                    <ItemTable key={category} title={category} items={items} />
                ))}
            </div>
            */}
            {/* Section: Revenue Breakdown and Public Health Performance */}
            <div className="space-y-6 mt-2">
                
                {/* Row 1: Dependency Items and Public Health Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Table 1: Liquor Dependency Items Revenue */}
                    {categoryData['Liquor Dependency Items Revenue'] ? (
                        <ItemTable 
                            title="Liquor Dependency Items Revenue" 
                            items={categoryData['Liquor Dependency Items Revenue']} 
                        />
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center text-slate-400 italic text-xs">
                            Liquor Dependency data not found
                        </div>
                    )}

                    {/* Table 2: Public Health Permit Fee Collection Performance */}
                    <PerformanceTable 
                        title="Public Health Permit Fee Collection Performance"
                        rows={[
                            { 
                                label: 'Paid (Revenue)', 
                                color: 'text-emerald-600',
                                isMoney: true, 
                                h1Value: data?.PHPRevH1,
                                h2Value: data?.PHPRevH2,
                                total: data?.PHPRevTotal 
                            },
                            {
                                label: 'Paid (Invoices)',
                                color: 'text-emerald-600',
                                isMoney: false, 
                                h1Value: data?.PHPCountH1,
                                h2Value: data?.PHPCountH2,
                                total: data?.PHPCountTotal
                            },
                            { 
                                label: 'Pending (Revenue)', 
                                color: 'text-red-600', 
                                isMoney: true,
                                h1Value: data?.PHPUnpaidRevH1,
                                h2Value: data?.PHPUnpaidCountH2 > 20 
                                    ? data?.PHPUnpaidRevH2 - (1000 * data?.PHPUnpaidCountH2) 
                                    : data?.PHPUnpaidRevH2,
                                total: data?.PHPUnpaidCountH2 > 20 
                                    ? data?.PHPUnpaidRevTotal - (1000 * data?.PHPUnpaidCountH2) 
                                    : data?.PHPUnpaidRevTotal
                            },
                            { 
                                label: 'Pending (Invoices)', 
                                color: 'text-red-600',
                                isMoney: false,
                                h1Value: data?.PHPUnpaidCountH1,
                                h2Value: data?.PHPUnpaidCountH2,
                                total: data?.PHPUnpaidCountTotal
                            },
                        ]}
                    />
                </div>

                {/* Row 2: License Items Revenue (Full Width) */}
                <div className="w-full">
                    {categoryData['Liquor License Items Revenue'] ? (
                        <ItemTable 
                            title="Liquor License Items Revenue" 
                            items={categoryData['Liquor License Items Revenue']} 
                        />
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center text-slate-400 italic text-xs">
                            Liquor License Items data not found
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

const DashboardHeader = ({ 
    selectedFY, setSelectedFY, 
    selectedSC, setSelectedSC, 
    subcounties, loadData, 
    lastUpdated, nextRefresh,
    loading, user, fyOptions 
}) => {
    const subCountyName = selectedSC 
        ? subcounties.find(sc => String(sc.id) === String(selectedSC))?.name || 'Selected Sub-County'
        : 'Entire County';

    return (
        <div className="bg-white px-8 py-4 rounded-b-2xl shadow-sm border-x border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col">
                <h1 className="text-xl font-black text-slate-800 flex items-center flex-wrap leading-tight">
                    <Beer className="mr-3 text-amber-600" size={22} /> 
                    Liquor License Performance
                    <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                    <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
                </h1>
                <span className="text-sm font-bold tracking-tighter mt-0.5">
                    Updated: <span className="text-red-600">{lastUpdated ? lastUpdated : '---'}</span>
                </span>
            </div>

            <span className="text-[12px] font-black text-slate-700 uppercase">
                    [ Next Dashboard Refresh is in: <span className="text-red-500">{nextRefresh} {nextRefresh !== 1 ? 'minutes' : 'minute'}</span> ]
                </span>
            <div className="flex items-center space-x-4">
                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase mr-3">FY Period</span>
                    <select 
                        value={selectedFY} 
                        onChange={(e) => setSelectedFY(e.target.value)}
                        className="bg-transparent text-sm font-black text-slate-700 outline-none border-none p-0 cursor-pointer"
                    >
                        <option value={fyOptions.previous}>{fyOptions.previous}</option>
                        <option value={fyOptions.current}>{fyOptions.current}</option>
                        <option value={fyOptions.next}>{fyOptions.next}</option>
                    </select>
                </div>

                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase mr-3">Region</span>
                    <select 
                        value={selectedSC} 
                        onChange={(e) => setSelectedSC(e.target.value)}
                        disabled={!!user?.sub_county_id}
                        className="bg-transparent text-sm font-bold text-slate-700 outline-none border-none cursor-pointer disabled:opacity-70"
                    >
                        {!user?.sub_county_id && <option value="">Entire County</option>}
                        {subcounties.map(sc => (
                            <option key={sc.id} value={sc.id}>{sc.name}</option>
                        ))}
                    </select>
                </div>

                <button 
                    onClick={loadData} 
                    className="p-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl shadow-md transition-all active:scale-95"
                >
                    <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>
        </div>
    );
};
const StatCard = ({ title, value, subValue, icon, color, isMoney }) => (
    <div className={`bg-white px-4 py-3 rounded-xl border-l-4 border-${color}-500 shadow-sm flex flex-col justify-center`}>
        <div className="flex justify-between items-center text-slate-400 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
            <div className={`text-${color}-500`}>{icon}</div>
        </div>
        
        <div className="text-xl font-black text-slate-800 leading-tight">
            {isMoney ? `KES ${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString()}
        </div>
        
        {subValue && (
            <div className="text-[11px] text-slate-400 mt-0.5 font-semibold truncate">
                {subValue}
            </div>
        )}
    </div>
);

const ItemTable = ({ title, items }) => {
    // 1. Calculate the category-wide Grand Totals (Footer)
    const grandTotals = items.reduce((acc, row) => {
        const h1 = Number(row.H1_Value || 0);
        const h2 = Number(row.H2_Value || 0);
        // Use the DB total if it exists and is not zero; otherwise, sum H1 and H2
        const rowTotal = Number(row.Total_Value) > 0 ? Number(row.Total_Value) : (h1 + h2);

        return {
            h1: acc.h1 + h1,
            h2: acc.h2 + h2,
            total: acc.total + rowTotal
        };
    }, { h1: 0, h2: 0, total: 0 });

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-6 py-2.5 border-b flex justify-between items-center">
                <h3 className="font-bold text-slate-700 text-sm">{title}</h3>
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">July - June Cycle</span>
            </div>
            
            <div className="overflow-y-auto max-h-[350px]">
                <table className="w-full text-left text-xs border-separate border-spacing-0">
                    <thead className="sticky top-0 z-20 text-slate-400 uppercase text-[9px] font-black tracking-wider bg-slate-50 shadow-sm">
                        <tr>
                            <th className="px-6 py-3 border-b">License Item</th>
                            <th className="px-6 py-3 text-center border-b">July - Dec (H1)</th>
                            <th className="px-6 py-3 text-center border-b">Jan - June (H2)</th>
                            <th className="px-6 py-3 text-right pr-10 text-blue-600 border-b">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {items.length > 0 ? (
                            items.map((row, idx) => {
                                // 2. Calculate the specific Row Total
                                const h1 = Number(row.H1_Value || 0);
                                const h2 = Number(row.H2_Value || 0);
                                const rowTotal = Number(row.Total_Value) > 0 ? Number(row.Total_Value) : (h1 + h2);

                                return (
                                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-2.5 font-bold text-slate-700">{row.Item}</td>
                                        <td className="px-6 py-2.5 text-center text-slate-500">
                                            {h1.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-2.5 text-center text-slate-500">
                                            {h2.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-2.5 text-right pr-10 font-black text-slate-800">
                                            {rowTotal.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="4" className="px-6 py-10 text-center text-slate-400 italic">No records found</td>
                            </tr>
                        )}
                    </tbody>
                    
                    {/* 3. Grand Total Footer Row */}
                    {items.length > 0 && (
                        <tfoot className="sticky bottom-0 z-20 bg-slate-100 border-t shadow-[0_-2px_4px_rgba(0,0,0,0.05)]">
                            <tr className="text-slate-900 font-black">
                                <td className="px-6 py-3 uppercase tracking-tighter text-[10px]">Grand Total</td>
                                <td className="px-6 py-3 text-center">
                                    {grandTotals.h1.toLocaleString()}
                                </td>
                                <td className="px-6 py-3 text-center">
                                    {grandTotals.h2.toLocaleString()}
                                </td>
                                <td className="px-6 py-3 text-right pr-10 text-blue-700">
                                    {grandTotals.total.toLocaleString()}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

{/*
const LiquorPerformanceTable = ({ title, data , fyStartYear, fyEndYear}) => {
    // Defining the rows based on the LQR procedure logic
    const pipelineRows = [
        { label: 'Inspections Stage',
            h1InspectionPending: data?.pendingInspectionH1,
            h1InspectionApproved: data?.inspectionApprovedH1,
            h2InspectionPending: data?.pendingInspectionH2,
            h2InspectionApproved: data?.inspectionApprovedH2,
            totalInspectionPending: data?.pendingInspection,
            totalInspectionApproved: data?.inspectionApproved
        }, 
        { label: 'Committee Stage',
            h1CommitteePending: data?.pendingCommitteeH1,
            h1CommitteeApproved: data?.committeeApprovedH1,
            h2CommitteePending: data?.pendingCommitteeH2,
            h2CommitteeApproved: data?.committeeApprovedH2,
            totalCommitteePending: data?.pendingCommittee,
            totalCommitteeApproved: data?.committeeApproved
        },
        { label: 'Full Approval',
            h1FinalPending: data?.pendingFinalH1,
            h1FinalApproved: data?.approvedH1,
            h2FinalPending: data?.pendingFinalH2,
            h2FinalApproved: data?.approvedH2,
            totalFinalPending: data?.pendingFinal,
            totalFinalApproved: data?.approvedTotal
        },
    ];

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-6 py-2.5 border-b flex justify-between items-center">
                <h3 className="font-bold text-slate-700 text-sm">{title}</h3>
                <span className="text-[10px] font-black text-amber-600 uppercase">Workflow Tracking</span>
            </div>
            <table className="w-full text-left text-xs">
                <thead className="text-slate-400 uppercase text-[9px] font-black tracking-wider bg-slate-50/30">
                    <tr>
                        <th className="px-2 py-2">Approval Stage</th>
                        <th className="px-6 py-2 text-center">Pending(H1)</th>
                        <th className="px-6 py-2 text-center">Approved(H1)</th>
                        <th className="px-6 py-2 text-center">Pending(H2)</th>
                        <th className="px-6 py-2 text-center">Approved(H2)</th> 
                        <th className="px-6 py-2 text-right pr-10 text-amber-800">Pending(FY)</th>
                        <th className="px-6 py-2 text-right pr-10 text-blue-800">Approved(FY)</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {pipelineRows.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-2 py-2 font-bold text-slate-700">
                                {row.label}
                            </td>
                            <td className="px-6 py-2 text-center text-slate-500">{row.h1InspectionPending ?? row.h1CommitteePending ?? row.h1FinalPending ?? 0}</td>
                            <td className="px-6 py-2 text-center text-slate-500">{row.h1InspectionApproved ?? row.h1CommitteeApproved ?? row.h1FinalApproved ?? 0}</td>
                            <td className="px-6 py-2 text-center text-slate-500">{row.h2InspectionPending ?? row.h2CommitteePending ?? row.h2FinalPending ?? 0}</td>
                            <td className="px-6 py-2 text-center text-slate-500">{row.h2InspectionApproved ?? row.h2CommitteeApproved ?? row.h2FinalApproved ?? 0}</td>
                            <td className="px-6 py-2 text-right pr-10 font-black text-slate-800">{row.totalInspectionPending ?? row.totalCommitteePending ?? row.totalFinalPending ?? 0}</td>
                            <td className="px-6 py-2 text-right pr-10 font-black text-slate-800">{row.totalInspectionApproved ?? row.totalCommitteeApproved ?? row.totalFinalApproved ?? 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="bg-amber-50/30 px-6 py-2 text-[10px] text-slate-800 italic border-t">
                * Data reflects applications created within the selected Financial Year boundaries.
                <span className="font-black">  H1: Jul - Dec {fyStartYear}| H2: Jan - June {fyEndYear}</span>
            </div>
        </div>
    );
};
*/}
const LiquorPerformanceTable = ({ title, data, fyStartYear, fyEndYear }) => {
    const pipelineRows = [
        { 
            label: 'Inspections',
            h1P: data?.pendingInspectionH1, h1A: data?.inspectionApprovedH1,
            h2P: data?.pendingInspectionH2, h2A: data?.inspectionApprovedH2,
            totP: data?.pendingInspection, totA: data?.inspectionApproved
        }, 
        { 
            label: 'Committee',
            h1P: data?.pendingCommitteeH1, h1A: data?.committeeApprovedH1,
            h2P: data?.pendingCommitteeH2, h2A: data?.committeeApprovedH2,
            totP: data?.pendingCommittee, totA: data?.committeeApproved
        },
        { 
            label: 'Full Approval',
            h1P: data?.pendingFinalH1, h1A: data?.approvedH1,
            h2P: data?.pendingFinalH2, h2A: data?.approvedH2,
            totP: data?.pendingFinal, totA: data?.approvedTotal
        },
    ];

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                <h3 className="font-bold text-slate-700 text-xs flex items-center">
                    <BarChart3 size={16} className="mr-2 text-slate-600" />
                    {title}
                </h3>
            </div>
            <table className="w-full text-left text-[11px]">
                <thead className="text-slate-400 uppercase text-[10px] font-black bg-slate-50/50">
                    <tr>
                        <th className="px-3 py-2">Stage</th>
                        {/* H1 Group */}
                        <th className="px-2 py-2 text-center border-l border-slate-100">
                            <div className="flex flex-col items-center" title="Pending H1">
                                <Clock size={13} className="text-amber-600 mb-0.5" />
                                <span>H1</span>
                            </div>
                        </th>
                        <th className="px-2 py-2 text-center">
                            <div className="flex flex-col items-center" title="Approved H1">
                                <CheckCircle2 size={13} className="text-emerald-600 mb-0.5" />
                                <span>H1</span>
                            </div>
                        </th>
                        {/* H2 Group */}
                        <th className="px-2 py-2 text-center border-l border-slate-100">
                            <div className="flex flex-col items-center" title="Pending H2">
                                <Clock size={13} className="text-amber-600 mb-0.5" />
                                <span>H2</span>
                            </div>
                        </th>
                        <th className="px-2 py-2 text-center">
                            <div className="flex flex-col items-center" title="Approved H2">
                                <CheckCircle2 size={13} className="text-emerald-600 mb-0.5" />
                                <span>H2</span>
                            </div>
                        </th> 
                        {/* FY Totals */}
                        <th className="px-2 py-2 text-center border-l border-slate-100 bg-amber-50/30">
                            <div className="flex flex-col items-center" title="Total Pending FY">
                                <Clock size={13} className="text-amber-600 mb-0.5" />
                                <span>FY</span>
                            </div>
                        </th>
                        <th className="px-2 py-2 text-center bg-blue-50/30">
                            <div className="flex flex-col items-center" title="Total Approved FY">
                                <CheckCircle2 size={13} className="text-blue-700 mb-0.5" />
                                <span>FY</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {pipelineRows.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-3 py-2 font-bold text-slate-700 truncate max-w-[80px]">
                                {row.label}
                            </td>
                            <td className="px-2 py-2 text-center text-slate-600 border-l border-slate-50">{row.h1P || 0}</td>
                            <td className="px-2 py-2 text-center text-slate-600">{row.h1A || 0}</td>
                            <td className="px-2 py-2 text-center text-slate-600 border-l border-slate-50">{row.h2P || 0}</td>
                            <td className="px-2 py-2 text-center text-slate-600">{row.h2A || 0}</td>
                            <td className="px-2 py-2 text-center font-black text-amber-800 border-l border-slate-50 bg-amber-50/20">{row.totP || 0}</td>
                            <td className="px-2 py-2 text-center font-black text-blue-800 bg-blue-50/20">{row.totA || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="bg-slate-50 px-4 py-1.5 text-[9px] text-slate-500 italic border-t flex justify-between">
                * Data reflects applications created within the selected Financial Year boundaries.
                <span className="font-black">  H1: Jul - Dec {fyStartYear}| H2: Jan - June {fyEndYear}</span>
                <span><Clock size={8} className="inline mr-1 text-amber-500" />Pending</span>
                <span><CheckCircle2 size={8} className="inline mr-1 text-emerald-500" />Approved</span>
            </div>
        </div>
    );
};
const PerformanceTable = ({ title, rows }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Reduced header padding from py-4 to py-2.5 */}
        <div className="bg-slate-50 px-6 py-2.5 border-b">
            <h3 className="font-bold text-slate-700 text-sm">{title}</h3>
        </div>
        <table className="w-full text-left text-xs">
            <thead className="text-slate-400 uppercase text-[9px] font-black tracking-wider bg-slate-50/30">
                <tr>
                    {/* Reduced th padding from py-3 to py-2 */}
                    <th className="px-6 py-2">Category</th>
                    <th className="px-6 py-2 text-center">July - Dec (H1)</th>
                    <th className="px-6 py-2 text-center">Jan - June (H2)</th>
                    <th className="px-6 py-2 text-right pr-10 text-blue-600">Total</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        {/* Reduced td padding from py-4 to py-2 */}
                        <td className={`px-6 py-2 font-bold ${row.color || 'text-slate-700'}`}>
                            {row.label}
                            {row.label === 'Pending RO' && (
                                <span className="ml-2 text-[9px] font-medium text-amber-600 italic">
                                    (Biller Approved)
                                </span>
                            )} 
                        </td>
                        <td className={`px-6 py-2 text-center ${row.color || 'text-slate-500'}`}>
                            {row.isMoney ? `KES ${Number(row.h1Value).toLocaleString()}` : row.h1Value}
                        </td>
                        <td className={`px-6 py-2 text-center ${row.color || 'text-slate-500'}`}>
                            {row.isMoney ? `KES ${Number(row.h2Value).toLocaleString()}` : row.h2Value}
                        </td>
                        <td className={`px-6 py-2 text-right pr-10 font-black ${row.color || 'text-slate-800'}`}>
                            {row.isMoney ? `KES ${Number(row.total).toLocaleString()}` : row.total}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
export default LiquorDashboard;